import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  useMemo,
} from "react";
import api from "api/api";
import { API_ENDPOINTS } from "constants/api";
import {
  applyOptimisticBalance,
  getAccountProfileErrorMessage,
  normalizeAccountProfile,
} from "domain/account";
import { useAuth } from "./AuthContext";

const UserProfileContext = createContext(null);

export const UserProfileProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchProfile = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.get(API_ENDPOINTS.USER.BALANCE);
      setProfile(normalizeAccountProfile(res.data));
    } catch (err) {
      setError(getAccountProfileErrorMessage(err));
      setProfile(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const updateBalanceOptimistically = useCallback((newBalance) => {
    setProfile((prev) => applyOptimisticBalance(prev, newBalance));
  }, []);

  const resetBalanceToServer = useCallback(() => {
    if (isAuthenticated) {
      fetchProfile();
    }
  }, [isAuthenticated, fetchProfile]);

  useEffect(() => {
    if (!isAuthenticated) {
      setProfile(null);
      setLoading(false);
      setError(null);
      return;
    }
    fetchProfile();
  }, [isAuthenticated, fetchProfile]);

  const value = useMemo(
    () => ({
      profile,
      loading,
      error,
      refresh: fetchProfile,
      updateBalanceOptimistically,
      resetBalanceToServer,
    }),
    [
      profile,
      loading,
      error,
      fetchProfile,
      updateBalanceOptimistically,
      resetBalanceToServer,
    ],
  );

  return (
    <UserProfileContext.Provider value={value}>
      {children}
    </UserProfileContext.Provider>
  );
};

export const useUserProfileContext = () => {
  const ctx = useContext(UserProfileContext);
  if (!ctx) {
    throw new Error(
      "useUserProfileContext must be used within a UserProfileProvider",
    );
  }
  return ctx;
};

export default UserProfileContext;
